/**
 * MUI Theme Configuration for Admin UI
 * White/clean theme with Tilly Green sage accent
 */

import { createTheme, ThemeOptions } from '@mui/material/styles';
import { colors, fonts, spacing, borderRadius } from './tokens';

const themeOptions: ThemeOptions = {
  palette: {
    mode: 'light',
    primary: {
      main: colors.accent, // #7FA897 (sage green)
      light: colors.accentLight, // #E6F0EB
      dark: colors.accentActive, // #5E8675
      contrastText: colors.white,
    },
    secondary: {
      main: colors.textMuted, // #8A948E
      contrastText: colors.white,
    },
    background: {
      default: colors.bg, // #F6F7F5
      paper: colors.surface, // #FFFFFF
    },
    text: {
      primary: colors.textPrimary, // #1F2933
      secondary: colors.textSecondary, // #5F6B66
    },
    success: { main: colors.success },
    warning: { main: colors.warning },
    error: { main: colors.error },
    divider: colors.border,
  },
  typography: {
    fontFamily: fonts.body,
    h1: {
      fontSize: '2rem',
      fontWeight: 600,
      lineHeight: 1.2,
      color: colors.textPrimary,
    },
    h2: {
      fontSize: '1.75rem',
      fontWeight: 600,
      lineHeight: 1.3,
      color: colors.textPrimary,
    },
    h3: {
      fontSize: '1.5rem',
      fontWeight: 600,
      lineHeight: 1.4,
      color: colors.textPrimary,
    },
    h4: {
      fontSize: '1.25rem',
      fontWeight: 600,
      lineHeight: 1.4,
      color: colors.textPrimary,
    },
    h5: {
      fontSize: '1rem',
      fontWeight: 600,
      lineHeight: 1.5,
      color: colors.textPrimary,
    },
    h6: {
      fontSize: '0.875rem',
      fontWeight: 600,
      lineHeight: 1.6,
      color: colors.textPrimary,
    },
    body1: {
      fontSize: '1rem',
      lineHeight: 1.5,
      color: colors.textPrimary,
    },
    body2: {
      fontSize: '0.875rem',
      lineHeight: 1.5,
      color: colors.textSecondary,
    },
    button: {
      fontWeight: 600,
      textTransform: 'none',
      fontSize: '0.95rem',
    },
    caption: {
      fontSize: '0.75rem',
      lineHeight: 1.4,
      color: colors.textMuted,
    },
  },
  shape: {
    borderRadius: borderRadius,
  },
  spacing: spacing,
  shadows: [
    'none',
    '0 1px 2px 0 rgba(31, 41, 51, 0.08)',
    '0 1px 3px 0 rgba(31, 41, 51, 0.1), 0 1px 2px 0 rgba(31, 41, 51, 0.06)',
    '0 4px 6px -1px rgba(31, 41, 51, 0.1), 0 2px 4px -1px rgba(31, 41, 51, 0.06)',
    '0 10px 15px -3px rgba(31, 41, 51, 0.1), 0 4px 6px -2px rgba(31, 41, 51, 0.05)',
    '0 20px 25px -5px rgba(31, 41, 51, 0.1), 0 10px 10px -5px rgba(31, 41, 51, 0.04)',
    '0 25px 50px -12px rgba(31, 41, 51, 0.25)',
    '0 25px 50px -12px rgba(31, 41, 51, 0.25)',
    '0 25px 50px -12px rgba(31, 41, 51, 0.25)',
    '0 25px 50px -12px rgba(31, 41, 51, 0.25)',
    '0 25px 50px -12px rgba(31, 41, 51, 0.25)',
    '0 25px 50px -12px rgba(31, 41, 51, 0.25)',
    '0 25px 50px -12px rgba(31, 41, 51, 0.25)',
    '0 25px 50px -12px rgba(31, 41, 51, 0.25)',
    '0 25px 50px -12px rgba(31, 41, 51, 0.25)',
    '0 25px 50px -12px rgba(31, 41, 51, 0.25)',
    '0 25px 50px -12px rgba(31, 41, 51, 0.25)',
    '0 25px 50px -12px rgba(31, 41, 51, 0.25)',
    '0 25px 50px -12px rgba(31, 41, 51, 0.25)',
    '0 25px 50px -12px rgba(31, 41, 51, 0.25)',
    '0 25px 50px -12px rgba(31, 41, 51, 0.25)',
    '0 25px 50px -12px rgba(31, 41, 51, 0.25)',
    '0 25px 50px -12px rgba(31, 41, 51, 0.25)',
    '0 25px 50px -12px rgba(31, 41, 51, 0.25)',
    '0 25px 50px -12px rgba(31, 41, 51, 0.25)',
  ],
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: colors.bg,
          color: colors.textPrimary,
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: borderRadius,
          textTransform: 'none',
          fontWeight: 600,
          padding: `${spacing * 0.75}px ${spacing * 1.5}px`,
        },
        contained: {
          boxShadow: 'none',
          '&:hover': {
            boxShadow: 'none',
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: borderRadius,
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: borderRadius,
          border: `1px solid ${colors.border}`,
          boxShadow: 'none',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: borderRadius,
          backgroundImage: 'none',
        },
      },
    },
  },
};

export const theme = createTheme(themeOptions);
